import { forwardRef, useMemo } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import H3 from '@base/H3/H3';
import Grow from '@base/Grow/Grow';
import H6 from '@base/H6/H6';
import P1 from '@base/P1/P1';
import Avatar from '@base/Avatar/Avatar';
import { formatMinutesFromSeconds } from '@utils/dates';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import pushRoute from '@utils/pushRoute';
import { useSelectorToStore } from '@hooks/reduxHooks';
import { mediaQuery, Spacings } from '@styles/index';
import BreadCrumbs from '@base/BreadCrumbs/BreadCrumbs';
import { getFreeCourseTags } from '@utils/breadcrumbs';
import { LESSON_PAGE } from '@constants/appFlyerLinks';
import useAppInstallAnalytics from '@hooks/useAppInstallAnalytics';
import { CONTINUE_ON_APP } from '@constants/segment';
import FreeLessonFrame from './FreeLessonFrame';

const { SPACING_6B, SPACING_10B } = Spacings;

const Wrapper = styled.div`
  background-color: var(--color-base-1);
  grid-column: ${({ $isDemo }) => ($isDemo ? 'span 24' : 'span 16')};
  border-radius: ${Spacings.SPACING_2B};
  box-shadow: ${boxShadowGenerator()};
  height: fit-content;
  ${mediaQuery.uptoMobile} {
    margin: 0 -${Spacings.SPACING_4B};
    border-radius: 0;
    box-shadow: none;
  }
`;

const StyH6 = styled(H6)`
  cursor: pointer;
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
`;
const LessonDetailsWrapper = styled.div`
  padding: ${SPACING_6B} ${SPACING_10B} ${SPACING_10B};
  ${mediaQuery.uptoMobile} {
    padding: ${Spacings.SPACING_4B};
  }
`;

const EducatorDetailsWrapper = styled.div`
  display: flex;
  margin-bottom: ${SPACING_6B};
`;

const Button = styled.a`
  height: 48px;
  text-align: center;
  vertical-align: middle;
  padding: ${({ padding }) => padding};
  margin: ${({ margin }) => margin};
  font-size: 16px;
  line-height: 100%;
  font-weight: bold;
  background-color: ${({ $bgColor }) => $bgColor};
  color: ${({ $color }) => $color};
  border: ${({ $border }) =>
    $border ? '1px solid var(--color-shadow)' : 'none'};
  border-radius: ${Spacings.SPACING_1B};
  ${mediaQuery.uptoMobile} {
    font-size: 14px;
    display: none;
  }
`;

const StyledBreadCrumbs = styled(BreadCrumbs)`
  padding: ${Spacings.SPACING_8B} ${SPACING_10B} 0;
  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const StyAvatar = styled(Avatar)`
  margin-right: ${Spacings.SPACING_4B};
`;

const StyH3 = styled(H3)`
  color: var(--color-text-primary);
  ${mediaQuery.uptoMobile} {
    font-size: 18px;
    line-height: 150%;
  }
`;

const StyP1 = styled(P1)`
  margin-bottom: ${Spacings.SPACING_8B};
  ${mediaQuery.uptoMobile} {
    margin-bottom: ${SPACING_6B};
  }
`;

const ContinueOnApp = styled.div`
  display: none;
  padding: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoMobile} {
    display: block;
    width: 100%;
    position: fixed;
    left: 0;
    bottom: 0;
    z-index: 2;
    background-color: var(--color-base-1);
    box-shadow: ${boxShadowGenerator({ yOffset: `-${Spacings.SPACING_4B}` })};
    & > a {
      line-height: 48px;
      width: 100%;
      display: block;
    }
  }
`;

const LessonDetails = ({ freeLessonInfo, courseInfo, isDemo }, ref) => {
  const {
    rank: lessonRank,
    recommendCount: upvotes,
    author,
    description
  } = freeLessonInfo || {};
  const lessonDuration =
    freeLessonInfo.videoDuration ||
    freeLessonInfo.video?.duration ||
    freeLessonInfo.duration ||
    0;
  const videoUrl = freeLessonInfo.videoUrl || freeLessonInfo.video?.url;
  const { user } = useSelectorToStore(({ auth }) => ({
    user: auth.me || {}
  }));
  const totalLessons = freeLessonInfo?.collection?.itemCount;
  const { firstName, lastName, avatar: thumbnail, username } = author;
  const lessonDurationFormated = formatMinutesFromSeconds(lessonDuration);
  const sendAapInstallAnalytics = useAppInstallAnalytics();
  const onEducatorClick = () => {
    pushRoute('/[authorID]', `/@${username}`);
  };
  const handleOnClick = () => {
    sendAapInstallAnalytics({
      button_text: CONTINUE_ON_APP
    });
  };

  const goalInfo = useMemo(
    () => ({
      ...(courseInfo?.goal ?? {}),
      goalSlug: courseInfo?.goal?.slug ?? ''
    }),
    [courseInfo?.goal]
  );

  return (
    <Wrapper $isDemo={isDemo} ref={ref}>
      {videoUrl ? (
        <FreeLessonFrame lessonDetails={freeLessonInfo} user={user} />
      ) : null}
      <StyledBreadCrumbs
        tags={getFreeCourseTags(goalInfo, courseInfo?.topologies?.[0])}
      />
      <LessonDetailsWrapper>
        <StyH3 as="h1">{freeLessonInfo.name || freeLessonInfo.title}</StyH3>
        {!isDemo && (
          <StyP1 color="var(--color-text-secondary)">
            {`Lesson ${lessonRank} of ${totalLessons} • ${upvotes} upvotes • ${lessonDurationFormated}`}
          </StyP1>
        )}
        {!isDemo && (
          <EducatorDetailsWrapper>
            <StyAvatar src={thumbnail} size="48px" onClick={onEducatorClick} />
            <StyH6 onClick={onEducatorClick} as="h4">
              {`${firstName} ${lastName}`}
            </StyH6>
            <Grow />
          </EducatorDetailsWrapper>
        )}
        <P1 color="var(--color-text-primary)">{description}</P1>
      </LessonDetailsWrapper>
      <ContinueOnApp>
        <Button
          $bgColor="var(--color-i-green)"
          $color="var(--color-base-1)"
          href={LESSON_PAGE}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleOnClick}
        >
          Continue on app
        </Button>
      </ContinueOnApp>
    </Wrapper>
  );
};

export default forwardRef(LessonDetails);

LessonDetails.propTypes = {
  freeLessonInfo: PropTypes.objectOf(PropTypes.any).isRequired,
  courseInfo: PropTypes.objectOf(PropTypes.any).isRequired,
  isDemo: PropTypes.bool
};

LessonDetails.defaultProps = {
  isDemo: false
};
